import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import CheckBox from '../../../../../components/common/CheckBox/CheckBox';
import { Button } from '../../../../../components/ui/Button/Button';
import S from './VideoSettingsItemStyles';
import useGetDeviceVideoSettings from '../../../../../API/hooks/videos/useGetDeviceVideoSettings';
import useGetDeviceVideoFleetSettings from '../../../../../API/hooks/videos/useGetDeviceVideoFleetSettings';
import usePostDeviceVideoSettings from '../../../../../API/hooks/videos/usePostDeviceVideoSettings';
import usePostDeviceVideoFleetSettings from '../../../../../API/hooks/videos/usePostDeviceVideoFleetSettings';
import { DeviceVideoSettingsPayload } from '../../../../../API/types/Videos';
import useGetFleetFeatures from '../../../../../API/hooks/useGetFleetFeatures';
import Modal from '../../../../../components/common/Modal/Modal';
import InfoModal from '../../../../../components/common/InfoModal/InfoModal';

type Props = {
  global?: boolean,
  fleetId: string,
  deviceId?: string,
  isOnline?: boolean;
  toggleModal: (closed: boolean) => void;
};

function VideoSettingsItem({
  global, fleetId, deviceId, isOnline, toggleModal,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const [confirmSaveModalOpen, toggleConfirmSaveModal] = useState(false);
  const [deviceOfflineModalOpen, toggleDeviceOfflineSaveModal] = useState(false);
  // const onSubmitSuccess = (): void => (toggleModal(false));
  const onSubmitSuccess = (): void => {
    if (!global && !isOnline) {
      toggleDeviceOfflineSaveModal(true);
    } else {
      toggleConfirmSaveModal(true);
    }
  };
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  const { liveVideo: liveVideoEnabled } = useGetFleetFeatures();
  const { data: fleetConfig, isLoading: isLoadingFleet, error: fleetFailure = {} } = useGetDeviceVideoFleetSettings(fleetId);
  const fleetConfigIsExisting = fleetConfig;
  const fleetConfigIsActive = fleetConfig?.isActive;
  // eslint-disable-next-line max-len, react-hooks/rules-of-hooks
  const { data: deviceSettings, isLoading, error: failure } = global ? useGetDeviceVideoFleetSettings(fleetId) : useGetDeviceVideoSettings(deviceId || '', fleetId);
  // eslint-disable-next-line max-len, react-hooks/rules-of-hooks
  const { mutate } = global ? usePostDeviceVideoFleetSettings(onSubmitSuccess, onSubmitFail) : usePostDeviceVideoSettings(onSubmitSuccess, onSubmitFail);
  const noConfig = failure && ((failure as any).response?.status === 404);
  const getConfigError = failure && ((failure as any).response?.status !== 404);

  const defaultConfig: any = {
    isActive: true,
    videoStreamEnable: true,
  };
  const formik = useFormik({
    initialValues: defaultConfig,
    onSubmit: (values) => {
      if (global && fleetId) {
        mutate({ ...values, fleetId } as unknown as DeviceVideoSettingsPayload);
      } else if (deviceId && fleetId) {
        delete values.isActive;
        mutate({
          ...values, deviceId, fleetId,
        } as unknown as DeviceVideoSettingsPayload);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    formik.setSubmitting(false);
  }, [submitError]);

  useEffect(() => {
    formik.setValues({
      videoStreamEnable: deviceSettings && deviceSettings.videoStreamEnable !== undefined ? deviceSettings.videoStreamEnable : false,
      isActive: deviceSettings ? deviceSettings.isActive : defaultConfig.isActive,
    }, false);
  }, [deviceSettings]);

  const handleCheckboxChange = (id: string, state: boolean): void => {
    formik.setFieldValue(id, state);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  const globalSettingIsActivated = fleetConfigIsExisting && fleetConfigIsActive && !global;

  if (isLoading || isLoadingFleet || getConfigError) {
    return (
      <S.Wrapper>
        <S.RowContainer style={{ display: 'block' }}>
          <p className={getConfigError ? 'loading error' : 'loading'}>
            {getConfigError ? 'Error of data fetching!' : 'Loading...'}
          </p>
        </S.RowContainer>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <p>Live Video Configuration: {isLoading || isLoadingFleet ? '  ... Loading ...' : ''}</p>
      <form onSubmit={formik.handleSubmit}>
        <div className="alerts-details-container">
          <S.RowContainer className={global ? '' : 'hidden'}>
            <CheckBox
              label="Apply to all devices in fleet"
              state={formik.values.isActive}
              onChange={handleCheckboxChange}
              name="isActive"
              disabled={isLoading || !liveVideoEnabled}
            />
          </S.RowContainer>
          <S.RowContainer className={globalSettingIsActivated ? 'hidden' : ''}>
            <div className="checkbox-element">
              <CheckBox
                label="Enable Live Video"
                key="videoStreamEnable"
                state={formik.values.videoStreamEnable}
                onChange={handleCheckboxChange}
                name="videoStreamEnable"
                disabled={(global && !formik.values.isActive) || isLoading || isLoadingFleet || !liveVideoEnabled}
              />
            </div>
          </S.RowContainer>
          <S.RowContainer className={!liveVideoEnabled ? '' : 'hidden'}>
            <p className="message warning">Warning! Video streaming is disabled. Please contact your administractor to turn on.</p>
          </S.RowContainer>
          <S.RowContainer className={globalSettingIsActivated ? '' : 'hidden'}>
            <p className="message warning">Global fleet setting was applied. No possibility to configure video streaming for individual device.</p>
          </S.RowContainer>
          <S.RowContainer className={!global && !globalSettingIsActivated && noConfig ? '' : 'hidden'}>
            <p className="message warning">Note: Device is using the default configuration. Please set/change new parameters if needed.</p>
          </S.RowContainer>
          <S.RowContainer className={global && !fleetConfigIsExisting ? '' : 'hidden'}>
            <p className="message warning">There is no fleet setting. Please set/change new parameters if needed.</p>
          </S.RowContainer>
          <S.RowContainer className={submitError ? '' : 'hidden'}>
            <p className="message error">Submitting error: {typeof submitError === 'string' ? submitError : submitError?.map((error) => error)}</p>
          </S.RowContainer>
        </div>
        <div className="actions-container">
          <Button
            height="50px"
            onClick={() => toggleModal(false)}
            type="button"
          >
            Close
          </Button>
          <Button
            className="save-button"
            height="50px"
            type="submit"
            disabled={
              formik.isSubmitting || !!submitError || (!global && (globalSettingIsActivated as boolean))
            }
          >
            Save
          </Button>
        </div>
      </form>
      {confirmSaveModalOpen && (
        <Modal
          title="Device settings"
          state={confirmSaveModalOpen}
          toggleState={toggleConfirmSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="New settings were submitted!"
            onClose={() => toggleConfirmSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {deviceOfflineModalOpen && (
        <Modal
          title="Device settings"
          state={deviceOfflineModalOpen}
          toggleState={toggleDeviceOfflineSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="Device is offline. Changes will be applied after device become online!"
            onClose={() => toggleDeviceOfflineSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default VideoSettingsItem;
